import { action, observable, computed, when, observe, reaction } from 'mobx';
import uuid from 'uuid/v4';

import { analytics } from 'core/analytics';
import { AppManager } from 'core/appManager';
import { Variables } from 'core/query';
import { Input } from 'core/mutation';
import { subscriptionStatusObservable } from 'core/client';
import { clientStore } from 'apps/client/app';

import {
  CreateMessageMutation,
  UpdateMessageMutation,
  DeleteMessageMutation,
  ContextMessagesQuery,
  ContextConversationQuery,
  IntermediatorConversationQuery,
  CreatorQuery,
  MessageCreatedSubscription,
  MessageDeletedSubscription,
  CreateAnnotationMutation,
  DeleteAnnotationMutation,
  AnnotationsQuery,
  PatchSubscription,
  ActiveParticipantStatusQuery,
  ParticipantStatusSubscription,
  ReadMessageMutation,
} from './gql';

import BidirectionalQueryManager from './BidirectionalQueryManager';

const maxReapplyAttempts = 1;
const messagesPageSize = 30;

const timestampToDate = timestamp => {
  if (typeof timestamp === 'string') {
    return Date.parse(timestamp);
  }
  return new Date(timestamp);
};

// NOTE (david): This function is used to update lastXMessage "pointers" by checking
// if new messages are newer. This needs to be checked because network can
// alter the order of operations. Furthermore, given two equally newer messages
// it returns the one with more data, this is useful when we are comparing
// locally constructed messages and server messages. Server messages have more
// data and are preferred. (Particularly id)
const getNewestMessageBetween = (message1, message2) => {
  if (!message1) return message2;
  if (!message2) return message1;

  // If they are different messages, return newest
  if (message1.tempKey !== message2.tempKey) {
    // if (message1.timestamp && message2.timestamp) {
    const date1 = timestampToDate(message1.timestamp);
    const date2 = timestampToDate(message2.timestamp);
    if (date1 > date2) return message1;
    if (date2 > date1) return message2;
    // }
  }

  // Else they have equal timestamp, or they are the same message.
  // Then return the object with more data. Since more data means
  // it's probably been constructed later.
  if (Object.keys(message1).length > Object.keys(message2).length)
    return message1;
  return message2;
};

export class ContextAppManager extends AppManager {
  static registryName = 'ContextAppManager';
  // annotations / messages
  @observable messages = [];
  @observable participants = [];
  @observable annotations = [];
  @observable replies = {};
  @observable members = {};
  @observable revisionMessages = {};
  @observable memberStatus = {};
  @observable remainingReapplyAttempts = maxReapplyAttempts;
  @observable firstMessageIndex = 0;
  @observable lastMessageIndex = 0;
  @observable lastUnreadMessageIndex = 0;

  @observable lastReadMessage = undefined;
  @observable lastMessage = undefined;
  @observable sendAsId = null;
  @observable initialTopMostItemIndex = 99999;
  @observable unreadCount = -1;
  annotationRetryTime = 0;
  associatedSpaceId = undefined;

  @observable viewerControl = null;

  constructor(parent, init = {}) {
    super(parent, init);
    this.variables = new Variables({
      contextId: () => this.context?.id,
      resourceId: this.resourceId,
      referenceId: () =>
        this.contextAppHolder.rfqApp?.id || this.contextAppHolder.order?.id,
    });

    // this.variables.onChange(['contextId', 'resourceId'], this.NewVariables);
    this.disposers.push(
      reaction(
        () => {
          return {
            contextId: this.context?.id,
            resourceId: this.resourceId(),
          };
        },
        () => {
          this.NewVariables();
        },
        { name: 'Reaction for: contextId, resourceId' },
      ),
    );

    // observe the client status
    // Not sure why we don't just add this to variables and 'onChange' above??
    this.subscriptionStatusDisposer = observe(
      subscriptionStatusObservable,
      change => {
        // check if it is reconnection
        if (
          change.oldValue === 'disconnected' &&
          change.newValue === 'connected'
        ) {
          // subscription reconnected query all the stuff again
          // which will also trigger re-subscribe
          this.queryAll();
        }
      },
    );
    this.disposers.push(this.subscriptionStatusDisposer);
  }

  @computed get context() {
    return this.contextAppHolder?.contextApp;
  }

  @action SetViewerControl = viewerControl => {
    this.viewerControl = viewerControl;
  };

  /*
    CadFile API
  */

  @computed get viewerFile() {
    // this.contextAppHolder ->
    //  - RFQAppManager        (src/apps/rfq/rfqPage/index.js)
    //  - SharedFileAppManager (src/apps/share/main.js)
    //  - FileViewerManager    (src/apps/project/tabs/file/filePage.js)
    if (this.contextAppHolder?.contribFile?.file)
      return this.contextAppHolder?.contribFile.file;

    if (this.contextAppHolder?.sharedFile?.revision?.file)
      return this.contextAppHolder?.sharedFile?.revision?.file;

    if (this.contextAppHolder?.file) return this.contextAppHolder.file;
    // ...
    return null;
  }

  NewVariables = () => {
    // reset messages and annotation
    this.messages = [];
    this.annotations = [];

    this.queryAll();
  };

  queryAll = () => {
    if (!clientStore.signedInProfile) return;
    this.QueryConversation();

    this.QueryAnnotations();

    // FIXME
    if (
      this.contextAppHolder?.constructor.registryName === 'SharedFileAppManager'
    ) {
      this.QueryActiveParticipants();
    }
  };

  compareUserActivity = (activityA, activityB) => {
    if (activityA.status === 'ONLINE') return -1;
    if (activityB.status === 'ONLINE') return 1;
    return 0;
  };

  @computed get usersActivity() {
    // FIXME
    if (
      !this.contextAppHolder ||
      !this.contextAppHolder.sharedFile ||
      this.contextAppHolder?.constructor.registryName !== 'SharedFileAppManager'
    ) {
      return [];
    }

    const sharedFileAppManager = this.contextAppHolder;
    const userList = [...sharedFileAppManager.sharedFile?.groups?.participants];
    return userList
      .filter(({ profile }) => !!profile)
      .map(({ invite, profile }) => {
        let status = invite
          ? 'INVITED'
          : this.memberStatus[profile.user?.id]?.activityStatus || 'OFFLINE';
        if (this.clientStore?.signedInProfile.user.id === profile.user?.id) {
          status = 'ONLINE';
        }
        return { status, profile };
      });
  }

  @computed get revisions() {
    if (
      this.contextAppHolder?.constructor.registryName === 'SharedFileAppManager'
    ) {
      return this.contextAppHolder.revisionsConn?.arrayApp.apps;
    }
    return [];
  }

  // FIXME - change once Context has proper permissions
  @computed get annotationsAvailable() {
    // any one who signed in
    return !!this.clientStore?.signedInProfile;
  }

  // FIXME - change once Context has proper permissions
  @computed get chatAvailable() {
    // any one who signed in
    return !!this.clientStore?.signedInProfile;
  }

  @action SetSendAsId = id => {
    this.sendAsId = id;
  };

  @action handleCreatorProfile = profile => {
    if (!profile?.user?.id) return;

    if (profile.avatar) {
      profile.avatar.src = profile.avatar.url;
    }
    this.members[profile.user.id] = profile;
  };

  @action onSendMessageSuccess = serverMessage => {
    this.lastMessage = getNewestMessageBetween(serverMessage, this.lastMessage);
    this.lastReadMessage = getNewestMessageBetween(
      serverMessage,
      this.lastReadMessage,
    );
  };

  @action onSendReplySuccess = message => {
    // create the key for the annotation reply
    if (!this.replies[message.referenceId])
      this.replies[message.referenceId] = [];
    this.replies[message.referenceId].push(message);
  };

  onSendMessageError = () => {};

  @action onUpdateMessageSuccess = ({ id, body }) => {
    const message = this.messages.find(item => item.id === id);

    message.body = body;
  };

  onUpdateMessageError = () => {};

  @action onDeleteMessageSuccess = (/* serverMessage */) => {};

  onDeleteMessageError = () => {};

  @action requestMessageCreator = createdBy => {
    if (!createdBy || this.members[createdBy]) return;

    // this.members[createdBy] = {};
    const creatorQuery = new CreatorQuery(this, {
      variables: { userId: createdBy },
    });
    creatorQuery.process(this.handleCreatorProfile);
    creatorQuery.query();
  };

  @action handleMessageDeletion = message => {
    this.requestMessageCreator(message.createdBy);
    // Ignore null messages or self-messages of the "regular" type
    // As those will be handled through the mutation handler
    if (!message) {
      return;
    }

    // check if this is a reply for annotation
    if (message.referenceId && message.messageType === 'regular') {
      // put in the map instead
      if (this.replies[message.referenceId]) {
        // if there is an existing one
        const index = this.replies[message.referenceId].findIndex(
          msg => msg.id === message.id,
        );
        if (index !== -1) {
          this.replies[message.referenceId].splice(index, 1);
        }
      }

      return;
    }

    // eslint-disable-next-line
    const index = this.messages.findIndex(msg => msg.id === message.id);
    if (index !== -1) {
      this.messages.splice(index, 1);
    }
  };

  @action handleMessageReceived = message => {
    this.requestMessageCreator(message.createdBy);
    // Ignore null messages or self-messages of the "regular" type
    // As those will be handled through the mutation handler
    if (!message) {
      return;
    }

    const m = Object.assign({}, message);

    // Sanity checking for the migration to conversation API Changes.
    // Now, the referenceId will refer to the rfq

    if (message.referenceId === this.variables.referenceId) {
      const index = this.messages.findIndex(
        msg =>
          msg.tempKey &&
          msg.messageType === 'regular' &&
          msg.tempKey === message.tempKey,
      );

      if (index !== -1) this.messages[index] = m;
      else this.messages.push(m);

      this.lastMessage = getNewestMessageBetween(message, this.lastMessage);

      if (message.createdBy === this.clientStore?.signedInProfile?.user?.id) {
        this.lastReadMessage = getNewestMessageBetween(
          message,
          this.lastReadMessage,
        );
      } else {
        this.unreadCount += 1;
      }
    } else {
      // TODO: Check this code. This is from the old annotations process
      // check if this is a reply for annotation
      if (message.referenceId && message.messageType === 'regular') {
        // put in the map instead
        if (this.replies[message.referenceId]) {
          // if there is an existing one
          this.replies[message.referenceId].push(message);
        } else {
          // never queried before
          const newReplies = [];
          newReplies.push(this.message);
          this.replies[message.referenceId] = newReplies;
        }

        return;
      }

      // if it is from other user
      if (message.createdBy !== this.clientStore?.signedInProfile?.user?.id) {
        this.messages.push(message);
        return;
      }

      // check if the tempKey is from this client
      const index = this.messages.findIndex(
        msg =>
          msg.tempKey &&
          msg.messageType === 'regular' &&
          msg.tempKey === message.tempKey,
      );
      if (index !== -1) {
        // there is a message pending with this
        // add the replace the temp message
        this.messages[index] = message;
        return;
      }

      // it is the same user but not the same client
      this.messages.push(message);
    }
  };

  onSendAnnotationSuccess = () => {
    this.viewerControl?.logInfo('annotation sent successfully');
  };

  onSendAnnotationError = () => {
    this.viewerControl?.reportError('error when sending annotation!!');
  };

  onQueryAnnotationsError = () => {
    this.viewerControl?.reportError('error when querying annotations!!');
  };

  onDeleteAnnotationSuccess = () => {
    this.viewerControl?.logInfo('annotation deleted successfully');
  };

  onDeleteAnnotationError = () => {
    this.viewerControl?.reportError('error when deleting annotation');
  };

  @action applyPatch = patch => {
    if (!this.viewerControl) {
      const status = {
        error: 1,
        reason: 'cannot apply patch to null ViewerControl',
      };
      console.error(status.reason);
      return status;
    }

    const status = new this.viewerControl.viewer.module.StatusEmbind();
    const { viewer } = this.viewerControl;
    if (viewer === null && !viewer?.isOk) {
      status.error = 1;
      status.reason = 'cannot apply patch to null viewer';
      this.viewerControl.reportError(status.reason);
      return status;
    }

    const { type, action: patchAction, data } = patch;
    const { id, tempKey } = data;
    if (type !== 'annotation') {
      status.error = 1;
      status.reason = 'unknown action patch type';
      this.viewerControl.reportError(status.reason);
      return status;
    }

    const { settings } = this.viewerControl.viewer;
    const { notePad } = this.viewerControl.cadFile;
    switch (patchAction) {
      case 'delete': {
        notePad.DeleteNote(id);
        break;
      }
      case 'add':
      default: {
        if (tempKey) {
          // new added pin note, delete the temp one...
          notePad.DeleteNote(tempKey);
          // if data contains a tempKey, it's considered invalid json by the viewer
          delete data.tempKey;
          // new added pin note expanded by default
          this.viewerControl.expandedPinId = id;
        }
        const jsonStr = JSON.stringify(data);
        notePad.AddNote(jsonStr, settings._wasm);
        const newNote = notePad.GetNote(id);
        if (newNote === null || !newNote.IsOk()) {
          status.error = 1;
          status.reason = 'cannot create note from patch';
          this.viewerControl.reportError(status.reason);
        }
        return status;
      }
    }
    return status;
  };

  @action handlePatch = patch => {
    if (!patch) return;

    this.viewerControl?.logInfo(`patch received: ${patch?.data?.id}`);

    // add or delete the reply key based on the action
    switch (patch.action) {
      case 'delete':
        delete this.replies[patch.data.id];
        break;
      case 'add':
        if (!this.replies[patch.data.id]) this.replies[patch.data.id] = [];
        break;
      default:
    }
    try {
      this.requestMessageCreator(patch.annotation?.createdBy);
      const status = this.applyPatch(patch);
      if (status.error !== 0) {
        throw new Error(
          `Error detected when applying annotation from subscription: ${status.reason}`,
        );
      }

      // HACK: hack to update the chat after pin note deltion
      // (handleMessageDeletion is never called when deleting pin note)
      if (patch && patch.action === 'delete') {
        // eslint-disable-next-line
        const index = this.messages.findIndex(
          msg => msg.referenceId === patch.data.id,
        );
        if (index !== -1) {
          this.messages.splice(index, 1);
        }
      }
    } catch (e) {
      this.handleApplyPatchError();
      analytics.logError(e);
    }
  };

  @action handleParticipantStatus = activityData => {
    this.memberStatus[activityData.userId] = activityData;
  };

  SubscribeParticipantStatus = () => {
    if (!this.clientStore.signedInProfile) return;

    if (this.participantStatusSubscription) {
      return;
    }

    this.participantStatusSubscription = new ParticipantStatusSubscription(
      this,
      {
        variables: {
          subscribeParticipantStatusInput: {
            contextId: this.variables.contextId,
            resourceId: this.variables.resourceId,
            referenceId: this.variables.referenceId,
          },
        },
      },
    );

    this.participantStatusSubscription.process(this.handleParticipantStatus);
    this.participantStatusSubscription.StartSubscription();
  };

  SubscribeMessageDeletion = () => {
    if (!this.clientStore.signedInProfile) return;

    if (this.messageDeletedSubscription) {
      this.messageDeletedSubscription.CompleteSubscription();
      this.messageDeletedSubscription = undefined;
    }

    if (!this.messageDeletedSubscription) {
      this.messageDeletedSubscription = new MessageDeletedSubscription(this, {
        variables: {
          subscribeMessageInput: {
            contextId: this.variables.contextId,
            resourceId: this.variables.resourceId,
            referenceId: this.variables.referenceId,
          },
        },
      });
      this.messageDeletedSubscription.process(this.handleMessageDeletion);
      this.messageDeletedSubscription.StartSubscription();
    }
  };

  SubscribeMessageCreation = () => {
    if (!this.clientStore.signedInProfile) return;

    if (this.messageCreatedSubscription) {
      this.messageCreatedSubscription.CompleteSubscription();
      this.messageCreatedSubscription = undefined;
    }
    if (!this.messageCreatedSubscription) {
      this.messageCreatedSubscription = new MessageCreatedSubscription(this, {
        variables: {
          subscribeMessageInput: {
            contextId: this.variables.contextId,
            resourceId: this.variables.resourceId,
            referenceId: this.variables.referenceId,
          },
        },
      });
      this.messageCreatedSubscription.process(this.handleMessageReceived);
      this.messageCreatedSubscription.StartSubscription();
    }
  };

  SubscribePatch = () => {
    if (!this.clientStore.signedInProfile) return;

    if (this.patchSubscription) {
      this.patchSubscription.CompleteSubscription();
    }
    this.patchSubscription = new PatchSubscription(this, {
      variables: {
        subscribePatchInput: {
          contextId: this.variables.contextId,
          resourceId: this.variables.resourceId,
          referenceId: this.variables.referenceId,
        },
      },
    });
    this.patchSubscription.process(this.handlePatch);
    this.patchSubscription.StartSubscription();
  };

  @action handleAnnotationReplies = (result, referenceId) => {
    if (!result?.nodes) return;
    // get the referenceId from the message
    if (result.nodes.length === 0) {
      this.replies[referenceId] = [];
      return;
    }
    this.replies[referenceId] = result.nodes;
  };

  /**
   * query all the messages that is involved with an annotation
   * @param referenceId Reference Id for messages e.g annotation id
   * @param messageType Type of message could be activity and regular
   */
  QueryAnnotationReplies = referenceId => {
    this.messagesQuery = new ContextMessagesQuery(this, {
      queryKey: `annotation-reply-${referenceId}`,
      onSysErr: this.onQueryMessagesError,
      variables: {
        // TODO: this is temporary, need to change with scrolling
        first: 5000,
        getMessagesInput: {
          contextId: this.variables.contextId,
          resourceId: this.variables.resourceId,
          referenceId,
          messageType: 'regular',
        },
      },
    });
    this.messagesQuery.process(result =>
      this.handleAnnotationReplies(result, referenceId),
    );
    this.messagesQuery.query();
  };

  @action handleMessages = result => {
    if (!result?.messages) return;
    this.messages = result.messages;
    this.SubscribeMessageCreation();
    this.SubscribeMessageDeletion();
  };

  @action handleMessageHistory = result => {
    if (!result?.messages) return;
    this.revisionMessages = {};
    result.messages.forEach(message => {
      const { resourceId } = message;
      if (resourceId === this.variables.resourceId) return;
      if (!this.revisionMessages[resourceId]) {
        this.revisionMessages[resourceId] = [];
      }
      this.revisionMessages[resourceId].push(message);
    });
  };

  QueryMessageHistory = () => {
    this.messageHistoryQuery = new ContextMessagesQuery(this, {
      queryKey: 'context-message-history',
      onSysErr: this.onQueryMessagesError,
      variables: {
        // TODO: this is temporary, need to change with scrolling
        first: 5000,
        getMessagesInput: {
          contextId: this.variables.contextId,
        },
      },
    });
    this.messageHistoryQuery.process(this.handleMessageHistory);
    this.messageHistoryQuery.query();
  };

  @action handleRevisionMessages = (version, result) => {
    if (!result?.messages) return;
    this.revisionMessages[version] = result.messages;
  };

  QueryRevisionMessages = version => {
    const revisionMessageQuery = new ContextMessagesQuery(this, {
      queryKey: `revision-${version}-messages`,
      onSysErr: this.onQueryMessagesError,
      variables: {
        // TODO: this is temporary, need to change with scrolling
        first: 5000,
        getMessagesInput: {
          contextId: this.variables.contextId,
          resourceId: version,
        },
      },
    });
    revisionMessageQuery.process(result =>
      this.handleRevisionMessages(version, result),
    );
    revisionMessageQuery.query();
  };

  @action handleApplyPatchError = () => {
    if (!this.remainingReapplyAttempts) return;
    // stop ongoing patch subscription if there's one,
    // to prevent handling new ones until state is cleared and reapplied
    if (this.patchSubscription) {
      this.patchSubscription.CompleteSubscription();
    }
    this.annotationsQuery.refetchQuery();
    this.remainingReapplyAttempts -= 1;
  };

  @action handleAnnotations = result => {
    if (!result?.annotations) return;
    // previously we mapped this.annotations and only added new
    // annotations, so this.annotations keeps old ones, but that
    // made it so the this.viewerControl.ClearNotes() would then
    // remove all notes from the wasm, and we would not re-add
    // the queried notes, so instead I just set this.annotations
    // here, and always add all of them, and also query all replies
    // for all of them
    this.annotations = result.annotations;
    // query all the replies of the annotations
    this.annotations.map(annot => this.QueryAnnotationReplies(annot.id));
    // wait until viewer and notepad are ready before applying patches
    const whenDisposer = when(
      () =>
        !!(
          this.viewerControl &&
          // I added viewerVisible here because cadReady can be true,
          // but the file we loaded is not the file for which we are
          // querying annotations here, so by checking viewerVisible &&
          // cadReady we're making sure we only attempt to add annotations
          // to the wasm once both viewer has been made visble and the
          // cadFile was loaded
          this.viewerControl.viewerVisible &&
          this.viewerControl.viewerReady &&
          this.viewerControl.cadReady
        ),
      () => {
        const requestedCreatorIds = [];

        try {
          this.viewerControl.ClearNotes();
          this.annotations.forEach(annotation => {
            const status = this.applyPatch({
              type: 'annotation',
              action: 'add',
              data: annotation,
            });
            if (status.error !== 0) {
              throw new Error(
                `Error detected when applying annotations: ${status.reason}`,
              );
            }

            const creatorId = annotation.createdBy;

            if (
              // eslint-disable-next-line no-prototype-builtins
              !this.members.hasOwnProperty(creatorId) &&
              !requestedCreatorIds.includes(creatorId)
            ) {
              // This is for avoiding querying multiple times the same id
              requestedCreatorIds.push(creatorId);
              this.requestMessageCreator(creatorId);
            }
          });
        } catch (e) {
          analytics.logError(e);
          this.handleApplyPatchError();
        }

        this.remainingReapplyAttempts = maxReapplyAttempts;
        this.SubscribePatch();
      },
    );
    this.disposers.push(whenDisposer);
  };

  QueryAnnotations = () => {
    if (!this.variables.contextId || !this.variables.resourceId) return;
    this.annotationsQuery = new AnnotationsQuery(this, {
      queryKey: 'resource-annotations',
      onSysErr: this.onQueryAnnotationsError,
      variables: {
        getAnnotationsInput: {
          contextId: this.variables.contextId,
          resourceId: this.variables.resourceId,
          referenceId: this.variables.referenceId,
        },
      },
    });
    this.annotationsQuery.process(this.handleAnnotations);
    this.annotationsQuery.query();
  };

  @action handleActiveParticipants = activeParticipants => {
    this.SubscribeParticipantStatus();
    if (!activeParticipants?.length) return;
    this.memberStatus = {};
    activeParticipants.forEach(activityData => {
      this.memberStatus[activityData.userId] = activityData;
    });
  };

  QueryActiveParticipants = () => {
    if (!this.variables.contextId || !this.variables.resourceId) return;
    this.activeParticipantsQuery = new ActiveParticipantStatusQuery(this, {
      queryKey: 'active-participants',
      onSysErr: this.onQueryActiveParticipants,
      variables: {
        getActiveParticipantStatus: {
          contextId: this.variables.contextId,
          resourceId: this.variables.resourceId,
        },
      },
    });
    this.activeParticipantsQuery.process(this.handleActiveParticipants);
    this.activeParticipantsQuery.query();
  };

  // helper function to extract the annotation id from the index
  GetAnnotationIdFromIndex = index => {
    const annotation = this.annotations && this.annotations[index];
    if (!annotation) return null;
    return annotation.id;
  };

  SendReply = (body, referenceId, optionsParameter) => {
    const tempKey = `temp-${uuid()}`;
    const options = optionsParameter || {
      onSuccess: result => this.onSendReplySuccess(result),
      onSysErr: this.onSendMessageError,
      onUserErr: this.onSendMessageError,
    };
    const input = new Input({
      contextId: this.variables.contextId,
      resourceId: this.variables.resourceId,
      sendAsId: this.sendAsId,
      referenceId,
      tempKey,
      body,
    });
    const mutation = CreateMessageMutation.New(input, options);
    this.mutations.push(mutation);
    return mutation;
  };

  SendMessage = body => {
    const tempKey = `temp-${uuid()}`;
    const currentTime = new Date();
    const createdById = this.clientStore?.signedInProfile?.user?.id;
    const profile = {
      avatar: this.clientStore?.signedInProfile?.avatar,
      fullName: this.clientStore?.signedInProfile?.fullName,
      slug: this.clientStore?.signedInProfile?.fullName,
    };
    const createdByObject = {
      id: createdById,
      profile,
    };
    const localMessage = {
      body,
      messageType: 'regular',
      timestamp: currentTime,
      createdBy: createdByObject,
      // TODO: alex add extra param
      contextId: this.variables.contextId,
      resourceId: this.variables.resourceId,
      tempKey,
    };

    this.requestMessageCreator(createdById);

    // Add message to the UI at the bottom, until actual response comes back
    this.messages.push(localMessage);
    this.lastMessage = localMessage;
    this.lastReadMessage = localMessage;

    const options = {
      onSuccess: result => this.onSendMessageSuccess(result),
      onSysErr: this.onSendMessageError,
      onUserErr: this.onSendMessageError,
    };
    const input = new Input({
      contextId: this.variables.contextId,
      resourceId: this.variables.resourceId,
      referenceId: this.variables.referenceId,
      sendAsId: this.sendAsId,
      tempKey,
      body,
    });
    const mutation = CreateMessageMutation.New(input, options);
    this.mutations.push(mutation);
    return mutation;
  };

  UpdateMessage = (messageId, body) => {
    const options = {
      onSuccess: result => this.onUpdateMessageSuccess(result),
      onSysErr: this.onUpdateMessageError,
      onUserErr: this.onUpdateMessageError,
    };

    const input = new Input({
      messageId,
      body,
    });

    UpdateMessageMutation.New(input, options);
  };

  DeleteMessage = (messageId, optionsParameter) => {
    const options = optionsParameter || {
      onSuccess: this.onDeleteMessageSuccess,
      onSysErr: this.onDeleteMessageError,
      onUserErr: this.onDeleteMessageError,
    };
    const input = new Input({
      messageId,
    });
    const mutation = DeleteMessageMutation.New(input, options);
    this.mutations.push(mutation);
    return mutation;
  };

  SendAnnotation = (body, tempKey, target, camera, optionsParameter) => {
    const options = optionsParameter || {
      onSuccess: this.onSendAnnotationSuccess,
      onSysErr: this.onSendAnnotationError,
      onUserErr: this.onSendAnnotationError,
    };

    // create the target with only the properties we need
    const annotationTarget = {
      targetType: target.targetType,
      selType: target.selType,
      instanceIx: target.instanceIx,
      brepIx: target.brepIx,
      bodyIx: target.bodyIx,
      prcPath: target.prcPath,
      faceHit: target.faceHit,
      faceIx: Number.isInteger(target.faceIx) ? target.faceIx : undefined,
      loopIx: Number.isInteger(target.loopIx) ? target.loopIx : undefined,
      edgeIx: Number.isInteger(target.edgeIx) ? target.edgeIx : undefined,
      pointHit: target.pointHit,
      pointIx: Number.isInteger(target.pointIx) ? target.pointIx : undefined,
      triangleIx: Number.isInteger(target.triangleIx)
        ? target.triangleIx
        : undefined,
      trianglePt: target.trianglePt || undefined,
      triangleNr: target.triangleNr || undefined,
    };

    const input = new Input({
      contextId: this.variables.contextId,
      resourceId: this.variables.resourceId,
      referenceId: this.variables.referenceId,
      tempKey,
      body,
      target: annotationTarget,
      camera,
      viewerState: {},
    });
    const mutation = CreateAnnotationMutation.New(input, options);
    this.mutations.push(mutation);
    return mutation;
  };

  DeleteAnnotation = (annotationId, optionsParameter) => {
    const options = optionsParameter || {
      onSuccess: this.onDeleteAnnotationSuccess,
      onSysErr: this.onDeleteAnnotationError,
      onUserErr: this.onDeleteAnnotationError,
    };
    const input = new Input({ annotationId });
    const mutation = DeleteAnnotationMutation.New(input, options);
    this.mutations.push(mutation);
    return mutation;
  };

  QueryConversation = () => {
    if (!this.variables.contextId || !this.variables.resourceId) return;

    let Query = ContextConversationQuery;

    if (this.clientStore.hasRole('INTERMEDIATOR')) {
      Query = IntermediatorConversationQuery;
    }

    this.superConversationQuery = new Query(this, {
      onSysErr: this.onQueryConversationError,
      variables: {
        contextId: this.variables.contextId,
        resourceId: this.variables.resourceId,
        referenceId: this.variables.referenceId,
        first: messagesPageSize,
      },
    });
    this.superConversationQuery.process(x => x, this.handleConversation);
    this.superConversationQuery.query();
  };

  onQueryConversationError = () => {
    console.error('error when querying conversation!!');
  };

  @action handleConversation = result => {
    this.SubscribeMessageCreation();
    this.SubscribeMessageDeletion();

    if (!result) {
      return;
    }

    // This is to discard converations being pushed by Apollo cache
    // that are no longer relevant.
    if (result.resourceId !== this.variables.resourceId) {
      return;
    }

    this.associatedSpaceId = result.space?.id;
    // If the associated space hasn't been resolved, it might be
    // due to flask taking to long to resolve.
    if (!this.associatedSpaceId) {
      analytics.sendEvent(
        'error-space-not-found-in-conversation',
        { missingSpaceForRfq: this.contextAppHolder?.rfqId },
        { ga: true },
      );
    }

    if (result.participants) {
      this.participants = result.participants;
    }
    // If there are no messages yet in the conversation,
    // result will be null. However, we still want to
    // subscribe to message creation and deletion
    if (result?.messages && this.messages.length === 0) {
      this.lastReadMessage = result.lastReadMessage;
      this.lastMessage = result.lastMessage;
      this.unreadCount = result.unreadCount;

      this.firstMessageIndex = Math.max(
        result.messages.total - result.messages.nodes.length,
        0,
      );

      this.lastMessageIndex = Math.max(result.messages.total - 1, 0);

      this.messageQueryManager = new BidirectionalQueryManager({
        startOffset: result.messages.total - result.messages.nodes.length,
        pageSize: messagesPageSize,
        contextId: this.variables.contextId,
        resourceId: this.variables.resourceId,
        referenceId: this.variables.referenceId,
        onPrevResultCb: r => {
          // Here I will receive the new bunch of messages
          let newMessages = r?.messages?.nodes;
          if (newMessages && newMessages.length > 0) {
            // Update the first message index (required for virtuoso)
            this.firstMessageIndex = Math.max(
              (this.firstMessageIndex -= newMessages.length),
              0,
            );

            // Warning! We are filtering here the messages because
            // for some unclear reason, the queries appears duplicated
            newMessages = newMessages.filter(
              msg => !this.messages.some(m => m.id === msg.id),
            );
            if (newMessages.length > 0) {
              this.messages = [...newMessages, ...this.messages];
            }
          }
        },
      });

      const processedIds = [];

      for (let i = 0; i < result.messages.nodes.length; i += 1) {
        const message = result.messages.nodes[i];
        const { createdBy } = message;
        const { id } = createdBy;

        if (!processedIds.includes(id)) {
          processedIds.push(id);

          this.members[id] = createdBy.profile;
        }
      }
      // ignore the messages that is already there
      this.messages = result.messages.nodes.filter(
        msg => !this.messages.some(m => m.id === msg.id),
      );
    }
  };

  @action setReadMessage = messageId => {
    if (!messageId) return;

    const options = {
      onSuccess: () => {
        if (this.lastReadMessage?.id !== messageId) {
          this.lastReadMessage = { id: messageId };
        }
      },
      onSysErr: this.onSetMessageReadError,
      onUserErr: this.onSetMessageReadError,
    };
    const input = new Input({
      messageId,
    });
    const mutation = ReadMessageMutation.New(input, options);
    this.mutations.push(mutation);
  };

  onSetMessageReadError = () => {
    console.error('error saving last read message status!!');
  };

  LoadPreviousMessageBatch = () => {
    if (!this.messageQueryManager) return;
    this.messageQueryManager.queryPreviousPage();
  };

  /* LoadNextMessageBatch = () => {
    if (!this.messageQueryManager) return;
    this.messageQueryManager.queryNextPage();
  }; */

  onQueryMessagesError = () => {
    console.error('error when querying messages!!');
  };
}
